import type { BlogDraft, UpdateDraftPayload } from './types'

/**
 * The API serializes SEO fields as flat properties (seoTitle, seoDescription,
 * seoKeywords) because the underlying Drizzle schema stores them as separate
 * columns. The client UI, form state, and PDF export were built around a
 * nested `seo: { title, description, keywords }` object. These transforms
 * bridge the two shapes at the API-client boundary so components don't need
 * to know about the flat names.
 */

type RawSeoFields = {
  seoTitle: string | null
  seoDescription: string | null
  seoKeywords: string[]
}

type RawBlogDraft = Omit<BlogDraft, 'seo'> & RawSeoFields

export function unwrapDraft(raw: RawBlogDraft): BlogDraft {
  const { seoTitle, seoDescription, seoKeywords, ...rest } = raw
  return {
    ...rest,
    seo: {
      title: seoTitle ?? '',
      description: seoDescription ?? '',
      keywords: seoKeywords ?? [],
    },
  }
}

/**
 * Map the nested UpdateDraftPayload to the flat shape the API's PATCH schema
 * expects. Subfields are omitted when undefined to preserve the partial-update
 * contract — sending `seoTitle: undefined` would erase the column on the
 * server's `if (input.seoTitle !== undefined)` guard, but omitting the key
 * leaves the existing value alone.
 */
export function packDraftPayload(payload: UpdateDraftPayload): Record<string, unknown> {
  const { seo, ...rest } = payload
  const result: Record<string, unknown> = { ...rest }
  if (seo) {
    if (seo.title !== undefined) result.seoTitle = seo.title
    if (seo.description !== undefined) result.seoDescription = seo.description
    if (seo.keywords !== undefined) result.seoKeywords = seo.keywords
  }
  return result
}
